import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import {
  auth,
  signInWithGoogle,
  signOut as fbSignOut,
  sendSignInLink,
  isEmailSignInLink,
  getStoredEmailForSignIn,
  clearStoredEmailForSignIn,
  completeEmailLinkSignIn,
} from '../firebase';
import { getMe, type MeResponse } from '../lib/api';

export type EmailLinkState = 'idle' | 'processing' | 'needs_email' | 'error';

// ---------------------------------------------------------------------------
// Auth + session bootstrap. On each signed-in user we call GET /api/me, which
// provisions the Firestore user doc and (on first login) sets the custom claims
// the security rules gate on. If /api/me reports `claimsUpdated`, we FORCE a
// token refresh before marking the session ready — otherwise the just-set
// `audience` claim isn't in the ID token yet and DIRECT Firestore reads (tracks,
// activity, progress) would be rejected by the rules. `isAuthorized` flips true
// only AFTER that refresh, so no Firestore-backed screen renders prematurely.
// ---------------------------------------------------------------------------

interface AuthContextValue {
  user: User | null;
  profile: MeResponse | null;
  loading: boolean;
  /** null = unknown/loading; true = /me ok + claims fresh; false = denied/error. */
  isAuthorized: boolean | null;
  authError: string | null;
  isAdmin: boolean;
  /** 'staff' | 'candidate' | null (unknown). UX only — server re-derives. */
  audience: 'staff' | 'candidate' | null;
  isCandidate: boolean;
  /** Magic-link completion state (blocks gated rendering while processing). */
  emailLinkState: EmailLinkState;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  /** Request a passwordless sign-in link to `email` (invited candidates). */
  sendLink: (email: string) => Promise<void>;
  /** Complete a magic-link sign-in when the email wasn't on this device (cross-device). */
  submitEmailForLink: (email: string) => Promise<void>;
  /** Re-fetch /api/me (e.g. after a role change). */
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setAuthorized] = useState<boolean | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [emailLinkState, setEmailLinkState] = useState<EmailLinkState>(() =>
    typeof window !== 'undefined' && isEmailSignInLink(window.location.href) ? 'processing' : 'idle',
  );

  // Bumped on every auth event / logout / retry so a late /me response from a
  // previous user can never apply after a sign-out or user switch.
  const genRef = useRef(0);
  const emailLinkHandledRef = useRef(false);
  const emailLinkHrefRef = useRef<string | null>(null);

  const fetchProfile = useCallback(async (u: User, gen: number) => {
    setAuthorized(null);
    setProfile(null);
    try {
      const res = await getMe();
      if (gen !== genRef.current) return;
      if (res.ok && (res.data as MeResponse)?.uid) {
        const me = res.data as MeResponse;
        // CRITICAL: if provisioning just set/changed claims, refresh the ID token
        // so the `audience` claim is present before any direct Firestore read.
        if (me.claimsUpdated) {
          try {
            await u.getIdToken(/* forceRefresh */ true);
          } catch {
            /* best-effort; reads may briefly fail until next rotation */
          }
          if (gen !== genRef.current) return;
        }
        setProfile(me);
        setAuthorized(true);
      } else {
        setProfile(null);
        setAuthorized(false);
      }
    } catch {
      if (gen !== genRef.current) return;
      setAuthorized(false); // fail closed
    }
  }, []);

  // Complete a magic-link sign-in (auto or cross-device). Fail-closed: invalidate
  // any prior session up front, and ALWAYS clear the stored email + URL after the
  // attempt so a failed attempt can't poison the next link.
  const runCompletion = useCallback(
    async (email: string, href: string) => {
      genRef.current++;
      setProfile(null);
      setAuthorized(null);
      setEmailLinkState('processing');
      try {
        await completeEmailLinkSignIn(email, href);
        setEmailLinkState('idle'); // onAuthStateChanged → fetchProfile resolves auth
      } catch {
        // Fail CLOSED: drop ANY session (even a pre-existing one) so no stale
        // authorized surface can render after a failed link completion.
        setEmailLinkState('error');
        setProfile(null);
        setAuthorized(false);
        void fbSignOut().catch(() => {});
      } finally {
        clearStoredEmailForSignIn();
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch {
          /* ignore */
        }
      }
    },
    [],
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const gen = ++genRef.current;
      setUser(u);
      setLoading(false);
      if (!u) {
        setProfile(null);
        setAuthorized(false);
        return;
      }
      void fetchProfile(u, gen);
    });
    return () => {
      genRef.current++;
      unsub();
    };
  }, [fetchProfile]);

  // On mount, complete a magic-link sign-in if the page was opened from one. Be
  // fail-closed from the first paint: capture the full href, IMMEDIATELY strip the
  // oobCode from the visible URL (we keep the href in a ref for completion), and
  // invalidate any prior session so a stale signed-in user can't render the app
  // while the link is being handled. Home/SignIn block gated rendering on
  // emailLinkState 'processing'/'needs_email'.
  useEffect(() => {
    if (emailLinkHandledRef.current) return;
    if (!isEmailSignInLink(window.location.href)) return;
    emailLinkHandledRef.current = true;
    emailLinkHrefRef.current = window.location.href;
    genRef.current++;
    setProfile(null);
    setAuthorized(null);
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      /* ignore */
    }
    const stored = getStoredEmailForSignIn();
    if (!stored) {
      // Cross-device: sign out any stale session, then prompt for the email.
      setEmailLinkState('needs_email');
      void fbSignOut().catch(() => {});
      return;
    }
    void runCompletion(stored, emailLinkHrefRef.current);
  }, [runCompletion]);

  const login = useCallback(async () => {
    setAuthError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed.';
      setAuthError(message);
      throw err;
    }
  }, []);

  const sendLink = useCallback(async (email: string) => {
    setAuthError(null);
    await sendSignInLink(email);
  }, []);

  const submitEmailForLink = useCallback(
    async (email: string) => {
      const href = emailLinkHrefRef.current;
      if (!href) {
        setEmailLinkState('error');
        return;
      }
      await runCompletion(email, href);
    },
    [runCompletion],
  );

  const logout = useCallback(async () => {
    genRef.current++;
    setProfile(null);
    setAuthorized(false);
    setAuthError(null);
    clearStoredEmailForSignIn();
    await fbSignOut();
  }, []);

  const getIdToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    return auth.currentUser.getIdToken(false);
  }, []);

  const refresh = useCallback(() => {
    const u = auth.currentUser;
    if (!u) return;
    const gen = ++genRef.current;
    void fetchProfile(u, gen);
  }, [fetchProfile]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      isAuthorized,
      authError,
      isAdmin: profile?.role === 'admin' && profile?.audience === 'staff',
      audience: profile?.audience ?? null,
      isCandidate: profile?.audience === 'candidate',
      emailLinkState,
      login,
      logout,
      getIdToken,
      sendLink,
      submitEmailForLink,
      refresh,
    }),
    [
      user,
      profile,
      loading,
      isAuthorized,
      authError,
      emailLinkState,
      login,
      logout,
      getIdToken,
      sendLink,
      submitEmailForLink,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
